import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ExternalLink, Tag, AlertCircle, DollarSign, 
  Clock, Zap, BarChart2, Package, Code,  CheckCircle, 
  XCircle, PieChart, BarChart as BarChartIcon, Info
} from 'lucide-react';
import {
  GroupedExtensionEvent,
  DetectedTag,
  TrackEvent,
  WebsiteScanSummary,
  TagCategory,
  WebRequestData,
  DataLayerEvent,
} from 'src/types/extension';
import { Button } from '@components/common/Button';
import { Card } from '@components/common/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@components/common/Table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/common/Accordion';
import { Badge } from '@components/common/Badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/common/Tooltip';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Legend, PieChart as RechartsPieChart, Pie, Cell 
} from 'recharts';
import LabsAppBar, { ActionButton, TabItem } from '@components/LabsAppBar';
import { createContract } from '../../services/contractApiService';
import { useNotifications } from '../../contexts/NotificationContext';

// Define tag categories and their colors for the pie chart
const TAG_CATEGORIES: Record<TagCategory, string> = {
  'Analytics': '#3b82f6', // blue-500
  'Advertising': '#10b981', // green-500
  'Marketing': '#8b5cf6', // purple-500
  'Support': '#f59e0b', // yellow-500
  'Essential': '#ef4444', // red-500
  'Other': '#6b7280', // gray-500
};

// Define MetaRouter supported tags
const METAROUTER_SUPPORTED_TAGS = [
  'Google Analytics', 
  'Google Tag Manager', 
  'Facebook Pixel', 
  'Segment', 
  'Mixpanel', 
  'Amplitude', 
  'HubSpot', 
  'Intercom', 
  'Optimizely',
  'Adobe Analytics',
  'Hotjar',
  'Fullstory',
  'Heap',
  'Pendo',
  'Kissmetrics'
];

interface SummaryCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  colorClass: string;
  tooltipContent?: string;
  status?: 'good' | 'warning' | 'bad';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ 
  title, 
  value, 
  description, 
  icon, 
  colorClass, 
  tooltipContent,
  status
}) => {
  // Add a status indicator color based on the status prop
  let statusColor = '';
  if (status === 'good') statusColor = 'border-l-4 border-green-500';
  if (status === 'warning') statusColor = 'border-l-4 border-yellow-500';
  if (status === 'bad') statusColor = 'border-l-4 border-red-500';

  return (
    <Card className={`p-4 flex flex-col items-start space-y-2 ${colorClass} ${statusColor}`}>
      <div className="flex items-center space-x-2">
        {icon}
        <h3 className="text-lg font-semibold">{title}</h3>
        {tooltipContent && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <AlertCircle className="h-4 w-4 text-gray-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipContent}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-sm text-gray-600">{description}</p>
    </Card>
  );
};

const ScanSummaryPage: React.FC = () => {
  const { domain } = useParams<{ domain?: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [groupedData, setGroupedData] = useState<GroupedExtensionEvent[]>([]);
  const [summary, setSummary] = useState<WebsiteScanSummary | null>(null);
  const [normalizationEvents, setNormalizationEvents] = useState<TrackEvent[]>([]);
  const [tagCategoryCounts, setTagCategoryCounts] = useState<{ name: string; value: number; color: string }[]>([]);
  const [clientVsServerData, setClientVsServerData] = useState<{ name: string; clientSide: number; serverSide: number }[]>([]);
  const [isCreatingContract, setIsCreatingContract] = useState<boolean>(false);

  useEffect(() => {
    const fetchAndProcessScanData = async () => {
      try {
        const response = await axios.get('/api/extension/events');
        const allData: GroupedExtensionEvent[] = response.data;

        let filteredData: GroupedExtensionEvent[] = [];
        if (domain) {
          // Filter data for the specific domain
          filteredData = allData.filter(group => group.domain === domain);
          if (filteredData.length === 0) {
            setError(`No scan data found for domain: ${domain}`);
          }
        } else {
          // If no domain param, show all data
          filteredData = allData;
        }

        setGroupedData(filteredData);
        processScanData(filteredData);
      } catch (err) {
        console.error('Error fetching scan data:', err);
        setError('Failed to load scan data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessScanData();
  }, [domain]);

  const processScanData = (data: GroupedExtensionEvent[]) => {
    let totalPageLoadTime = 0;
    let pageLoadCount = 0;
    let totalLCP = 0;
    let lcpCount = 0;
    const uniqueTags = new Set<string>();
    let totalTagLoadTime = 0;
    let tagLoadCount = 0;
    let brokenTagRequests = 0;
    const allTrackEvents: TrackEvent[] = [];
    const categoryCountMap: Record<TagCategory, number> = {
      'Analytics': 0,
      'Advertising': 0,
      'Marketing': 0,
      'Support': 0,
      'Essential': 0,
      'Other': 0
    };

    // Process all data to calculate metrics
    data.forEach((group: GroupedExtensionEvent) => {
      // Process web requests for page load time, LCP, and broken requests
      group.webRequests.forEach((req: WebRequestData) => {
        if (req.type === 'page_load' && req.duration) {
          totalPageLoadTime += req.duration;
          pageLoadCount++;
        }
        if (req.type === 'lcp' && req.duration) {
          totalLCP += req.duration;
          lcpCount++;
        }
        if (req.statusCode && req.statusCode >= 400) {
          brokenTagRequests++;
        }
      });

      // Process detected tags
      group.detectedTags.forEach((tag: DetectedTag) => {
        uniqueTags.add(tag.name);
        
        // Count tags by category
        if (tag.category && categoryCountMap[tag.category]) {
          categoryCountMap[tag.category]++;
        } else {
          categoryCountMap['Other']++;
        }
        
        // Calculate tag load time
        if (tag.loadTime) {
          totalTagLoadTime += tag.loadTime;
          tagLoadCount++;
        }
      });

      // Process tag load events
      group.detectedTagLoadEvents.forEach((tagLoadEvent) => {
        uniqueTags.add(tagLoadEvent.name);
        if (tagLoadEvent.loadTime) {
          totalTagLoadTime += tagLoadEvent.loadTime;
          tagLoadCount++;
        }
      });

      // Process track events
      group.dataLayerEvents.forEach((event: DataLayerEvent) => {
        if (event.event && event.type === 'track') {
          allTrackEvents.push({ 
            eventName: event.event, 
            count: event.data?.count || 1,
            properties: event.data
          });
        }
      });
    });

    // Calculate summary metrics
    const avgPageLoadTime = pageLoadCount > 0 ? (totalPageLoadTime / pageLoadCount / 1000).toFixed(2) : 'N/A';
    const avgLCP = lcpCount > 0 ? (totalLCP / lcpCount / 1000).toFixed(2) : 'N/A';
    const avgTagLoadTime = tagLoadCount > 0 ? (totalTagLoadTime / tagLoadCount).toFixed(0) : 'N/A';
    const approximateTagsMetaRouterCanRemove = calculateMetaRouterSavings(Array.from(uniqueTags));

    // Set summary data
    setSummary({
      avgPageLoadTime: avgPageLoadTime,
      largestContentfulPaint: avgLCP,
      uniqueTagsDetected: uniqueTags.size,
      avgTagLoadTime: avgTagLoadTime,
      brokenTagRequests: brokenTagRequests,
      approximateTagsMetaRouterCanRemove: approximateTagsMetaRouterCanRemove,
    });

    // Process normalization events
    const normalizationMap = new Map<string, TrackEvent>();
    allTrackEvents.forEach(event => {
      if (normalizationMap.has(event.eventName)) {
        const existingEvent = normalizationMap.get(event.eventName)!;
        existingEvent.count += event.count;
      } else {
        normalizationMap.set(event.eventName, { ...event });
      }
    });
    setNormalizationEvents(Array.from(normalizationMap.values()));

    // Prepare tag category data for pie chart
    const categoryData = Object.entries(categoryCountMap)
      .filter(([_, count]) => count > 0)
      .map(([category, count]) => ({
        name: category,
        value: count,
        color: TAG_CATEGORIES[category as TagCategory] || TAG_CATEGORIES['Other']
      }));
    setTagCategoryCounts(categoryData);

    // Prepare client vs server side data for bar chart
    const clientSideTags = uniqueTags.size;
    const serverSideTags = approximateTagsMetaRouterCanRemove;
    setClientVsServerData([
      {
        name: 'Current',
        clientSide: clientSideTags,
        serverSide: 0
      },
      {
        name: 'With MetaRouter',
        clientSide: clientSideTags - serverSideTags,
        serverSide: serverSideTags
      }
    ]);
  };

  const calculateMetaRouterSavings = (tags: string[]): number => {
    // Check against the list of supported tags
    return tags.filter(tag => METAROUTER_SUPPORTED_TAGS.includes(tag)).length;
  };

  const estimatePageLoadSavings = (numThirdPartyScripts: number): string => {
    // Estimate load savings: 100-200ms per script
    const minSavings = numThirdPartyScripts * 100;
    const maxSavings = numThirdPartyScripts * 200;
    if (numThirdPartyScripts === 0) return '0ms';
    return `${minSavings}-${maxSavings}ms`;
  };

  const getTagCategoryBadge = (category: TagCategory) => {
    let color = 'bg-gray-200 text-gray-800';
    switch (category) {
      case 'Analytics':
        color = 'bg-blue-100 text-blue-800';
        break;
      case 'Advertising':
        color = 'bg-green-100 text-green-800';
        break;
      case 'Marketing':
        color = 'bg-purple-100 text-purple-800';
        break;
      case 'Support':
        color = 'bg-yellow-100 text-yellow-800';
        break;
      case 'Essential':
        color = 'bg-red-100 text-red-800';
        break;
      default:
        break;
    }
    return <Badge className={color}>{category}</Badge>;
  };

  const handleCreateContract = async (eventName: string, properties?: any) => {
    try {
      setIsCreatingContract(true);
      
      // Create a new contract with the event data
      const contractData = {
        description: `Auto-generated contract from website scan for event: ${eventName}`,
        schemaIds: [], // Will be selected in the wizard
        integrationIds: [], // Will be selected in the wizard
        events: [
          {
            id: crypto.randomUUID(),
            name: eventName,
            properties: properties ? Object.keys(properties).map(key => ({
              id: crypto.randomUUID(),
              name: key,
              path: key,
              type: typeof properties[key],
              value: properties[key],
              isRequired: false
            })) : []
          }
        ],
        normalizedProperties: {},
        mappingProgress: {
          total: 0,
          mapped: 0,
          required: 0,
          requiredMapped: 0
        }
      };

      // Create the contract
      const contract = await createContract({
        name: `${eventName} Contract`,
        schema_id: '', // Will be selected in the wizard
        data: contractData
      });

      showNotification('success', 'Contract created successfully');
      
      // Navigate to the contract edit page
      navigate(`/labs/contracts/${contract.id}/edit`);
    } catch (error) {
      console.error('Failed to create contract:', error);
      showNotification('error', 'Failed to create contract');
    } finally {
      setIsCreatingContract(false);
    }
  };

  // Determine status for summary cards
  const getPageLoadTimeStatus = (time: string): 'good' | 'warning' | 'bad' | undefined => {
    if (time === 'N/A') return undefined;
    const seconds = parseFloat(time);
    if (seconds <= 3) return 'good';
    if (seconds <= 6) return 'warning';
    return 'bad';
  };

  const getLCPStatus = (time: string): 'good' | 'warning' | 'bad' | undefined => {
    if (time === 'N/A') return undefined;
    const seconds = parseFloat(time);
    if (seconds <= 2.5) return 'good';
    if (seconds <= 4) return 'warning';
    return 'bad';
  };

  const getTagLoadTimeStatus = (time: string): 'good' | 'warning' | 'bad' | undefined => {
    if (time === 'N/A') return undefined;
    const ms = parseFloat(time);
    if (ms <= 100) return 'good';
    if (ms <= 200) return 'warning';
    return 'bad';
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color || entry.fill }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Define breadcrumbs and actions for LabsAppBar
  const breadcrumbs = [
    { name: 'Website Scans', iconName: 'FileChartColumn', path: '/labs/chrome-extension' },
    { name: domain ? `${domain}` : 'Scan Summary' }
  ];

  const actions: ActionButton[] = [
    { 
      label: 'Back to Scans', 
      onClick: () => navigate('/labs/chrome-extension'), 
      iconName: 'ArrowLeft', 
      variant: 'secondary' 
    }
  ];

  // Define tabs for LabsAppBar
  const tabs: TabItem[] = [
    { label: 'Scan Overview', path: domain ? `/labs/chrome-extension/${domain}` : '/labs/chrome-extension' },
    { label: 'Tag Map', path: domain ? `/labs/chrome-extension/${domain}/tag-map` : '/labs/chrome-extension/tag-map' }
  ];

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <LabsAppBar breadcrumbs={breadcrumbs} actions={actions} tabs={tabs} />
        <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <LabsAppBar breadcrumbs={breadcrumbs} actions={actions} tabs={tabs} />
        <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 max-w-2xl">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error</h3>
                <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => navigate('/labs/chrome-extension')}>
                    Back to Scans
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar breadcrumbs={breadcrumbs} actions={actions} tabs={tabs} />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-10 max-w-9xl mx-auto">

          {/* Header Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SummaryCard
                title="Average Page Load Time"
                value={`${summary.avgPageLoadTime} sec`}
                description="Overall average page load duration."
                icon={<Clock className="h-6 w-6 text-blue-600" />}
                colorClass="bg-blue-50 dark:bg-blue-900/20"
                tooltipContent="Time until the page is fully loaded and interactive."
                status={getPageLoadTimeStatus(summary.avgPageLoadTime)}
              />
              <SummaryCard
                title="Largest Contentful Paint"
                value={`${summary.largestContentfulPaint} sec`}
                description="Average LCP, a key Core Web Vital."
                icon={<Zap className="h-6 w-6 text-purple-600" />}
                colorClass="bg-purple-50 dark:bg-purple-900/20"
                tooltipContent="Measures perceived load speed: when the largest content element is visible."
                status={getLCPStatus(summary.largestContentfulPaint)}
              />
              <SummaryCard
                title="Unique Tags Detected"
                value={summary.uniqueTagsDetected}
                description="Total distinct client-side tags found."
                icon={<Tag className="h-6 w-6 text-green-600" />}
                colorClass="bg-green-50 dark:bg-green-900/20"
                tooltipContent="Number of unique third-party scripts or pixels detected."
              />
              <SummaryCard
                title="Average Tag Load Time"
                value={`${summary.avgTagLoadTime} ms`}
                description="Average time for individual tags to load."
                icon={<BarChart2 className="h-6 w-6 text-orange-600" />}
                colorClass="bg-orange-50 dark:bg-orange-900/20"
                tooltipContent="Indicates performance impact of individual tags."
                status={getTagLoadTimeStatus(summary.avgTagLoadTime)}
              />
              <SummaryCard
                title="Broken Tag Requests"
                value={summary.brokenTagRequests}
                description="Requests with HTTP 400+ or 500+ status codes."
                icon={<AlertCircle className="h-6 w-6 text-red-600" />}
                colorClass="bg-red-50 dark:bg-red-900/20"
                tooltipContent="These tags failed to load or responded with an error, potentially impacting data collection."
                status={summary.brokenTagRequests > 0 ? 'warning' : 'good'}
              />
              <SummaryCard
                title="Approx. Tags MetaRouter Can Remove"
                value={summary.approximateTagsMetaRouterCanRemove}
                description="Based on internal server-side integrations."
                icon={<Package className="h-6 w-6 text-teal-600" />}
                colorClass="bg-teal-50 dark:bg-teal-900/20"
                tooltipContent="Estimate of tags that can be migrated to MetaRouter's server-side platform, reducing client-side load."
              />
            </div>
          )}

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client-Side vs Server-Side Load Potential */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <BarChartIcon className="h-5 w-5 mr-2 text-blue-600" />
                Client-Side vs Server-Side Load Potential
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={clientVsServerData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis label={{ value: 'Number of Tags', angle: -90, position: 'insideLeft' }} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="clientSide" name="Client-Side Tags" fill="#ef4444" /> {/* red-500 */}
                    <Bar dataKey="serverSide" name="Server-Side Tags" fill="#10b981" /> {/* green-500 */}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                This chart shows the potential reduction in client-side tags by migrating to MetaRouter's server-side platform.
              </p>
            </Card>

            {/* Tag Category Distribution */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <PieChart className="h-5 w-5 mr-2 text-purple-600" />
                Tag Category Distribution
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={tagCategoryCounts}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {tagCategoryCounts.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                This chart shows the distribution of detected tags by category.
              </p>
            </Card>
          </div>

          {/* Deep Dive Sections */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-8">Deep Dive by Domain</h2>
          <Accordion type="multiple" className="w-full">
            {groupedData.map((group, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="flex justify-between items-center py-4 px-6 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center">
                    <ExternalLink className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                    {group.domain}
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="p-6 bg-white dark:bg-gray-800 border border-t-0 dark:border-gray-700 rounded-b-lg shadow-sm">
                  <div className="space-y-6">
                    {/* Detected Tags Table */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3 flex items-center">
                        <Tag className="h-5 w-5 mr-2 text-gray-700 dark:text-gray-300" /> Detected Tags
                      </h4>
                      {group.detectedTags.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Tag Name</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead>Count</TableHead>
                              <TableHead>Server-Side Supported</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.detectedTags.map((tag: DetectedTag, tagIndex: number) => (
                              <TableRow key={tagIndex}>
                                <TableCell className="font-medium">{tag.name}</TableCell>
                                <TableCell>{getTagCategoryBadge(tag.category)}</TableCell>
                                <TableCell>{tag.count}</TableCell>
                                <TableCell>
                                  {METAROUTER_SUPPORTED_TAGS.includes(tag.name) ? (
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <XCircle className="h-5 w-5 text-red-500" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">No tags detected for this domain.</p>
                      )}
                    </div>

                    {/* Track Events Table */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3 flex items-center">
                        <Code className="h-5 w-5 mr-2 text-gray-700 dark:text-gray-300" /> Track Events
                      </h4>
                      {group.dataLayerEvents.filter(e => e.type === 'track').length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Event Name</TableHead>
                              <TableHead>Count</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.dataLayerEvents
                              .filter((e: DataLayerEvent) => e.type === 'track')
                              .map((event: DataLayerEvent, eventIndex: number) => (
                                <TableRow key={eventIndex}>
                                  <TableCell className="font-medium">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <div className="flex items-center">
                                            <span>{event.event}</span>
                                            <Info className="h-4 w-4 ml-1 text-gray-400" />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {event.data && Object.keys(event.data).length > 0 ? (
                                            <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                              {JSON.stringify(event.data, null, 2)}
                                            </pre>
                                          ) : (
                                            <span>No additional properties.</span>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </TableCell>
                                  <TableCell>{event.data?.count || 1}</TableCell>
                                  <TableCell>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleCreateContract(event.event, event.data)}
                                      disabled={isCreatingContract}
                                    >
                                      {isCreatingContract ? 'Creating...' : 'Create Data Contract'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">No track events detected for this domain.</p>
                      )}
                    </div>

                    {/* Broken Tags List */}
                    {group.webRequests.filter(req => req.statusCode && req.statusCode >= 400).length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold mb-3 flex items-center text-red-700 dark:text-red-400">
                          <AlertCircle className="h-5 w-5 mr-2" /> Broken Tag Requests
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                          {group.webRequests
                            .filter((req: WebRequestData) => req.statusCode && req.statusCode >= 400)
                            .map((req: WebRequestData, reqIndex: number) => (
                              <li key={reqIndex}>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <span className="font-medium">{req.url}</span> (Status: {req.statusCode})
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Status: {req.statusCode} {req.statusLine}</p>
                                      <p>Method: {req.method}</p>
                                      <p>Type: {req.type}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}

                    {/* Potential Page Load Savings */}
                    <div>
                      <h4 className="text-lg font-semibold mb-3 flex items-center">
                        <DollarSign className="h-5 w-5 mr-2 text-green-700 dark:text-green-500" /> Potential Page Load Savings
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300">
                        Estimate: {estimatePageLoadSavings(group.detectedTags.length)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        (Based on {group.detectedTags.length} 3rd-party scripts × 100–200ms per script)
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Normalization Tooling */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-8">Normalization Tooling: All Track Events</h2>
          <Card className="p-6">
            {normalizationEvents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event Name</TableHead>
                    <TableHead>Occurrences</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {normalizationEvents.map((event: TrackEvent, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="flex items-center">
                                <span>{event.eventName}</span>
                                <Info className="h-4 w-4 ml-1 text-gray-400" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {event.properties && Object.keys(event.properties).length > 0 ? (
                                <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                  {JSON.stringify(event.properties, null, 2)}
                                </pre>
                              ) : (
                                <span>No additional properties.</span>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>{event.count}</TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleCreateContract(event.eventName, event.properties)}
                          disabled={isCreatingContract}
                        >
                          {isCreatingContract ? 'Creating...' : 'Make Contract'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">No track events detected across all scans for normalization.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScanSummaryPage;
